﻿import React from 'react';
import { useQuery } from 'react-query';
import Skeleton from '@mui/material/Skeleton';
import DigitalStylingOrderCustomerEntry from './DigitalStylingOrderCustomerEntry';
import { Translate } from '../../../../../../shared/LanguageService';
import { UserRole } from '../../../../../../components/common/header/photo/head-bar/newsComponent/utils/userRoleHelper';
import { DigitalStylingOrderAdminView } from '../../adminView/entry/view/DigitalStylingOrderAdminView';
import { DigitalStylingOrderCustomerView } from './DigitalStylingOrderCustomerView';
import { CustomerDigitalStylingApi } from '../queries/CustomerDigitalStylingApi';
import { DigitalStylingStatus } from '../../shared/types';

type DigitalStylingCustomerViewDelegateProps = {
    orderStatus: number;
} & ExternalProps;

type ExternalProps = {
    isCustomer: boolean;
    isNewOrder: boolean;
    orderId: number;
    userRole: string;
};

function DigitalStylingCustomerViewDelegate({
    orderStatus,
    isCustomer,
    isNewOrder,
    orderId,
    userRole
}: DigitalStylingCustomerViewDelegateProps) {
    if (isNewOrder || (isCustomer && orderStatus === DigitalStylingStatus.Created)) {
        return (
            <DigitalStylingOrderCustomerEntry
                labels={{
                    tabPanelTitle: Translate('digital-styling/tab/customer-upload'),
                    filesAdded: Translate('photographer-uploads/order/row/files-added'),
                    defaultMessage: Translate('dropzone/title'),
                    fallbackMessage: Translate('dropzone/fallback'),
                    invalidFileType: Translate('dropzone/invalid-filetype'),
                    cancelUpload: Translate('dropzone/cancel-upload'),
                    cancelUploadConfirmation: Translate('dropzone/cancel-upload-confirmation'),
                    uploadButton: Translate('dropzone/button/upload'),
                    thumbnailTooltipDelete: Translate(
                        'image-processing/page/list/thumbnail/title/delete'
                    ),
                    confirmDeleteSingleTitle: Translate('delete/modal/title/delete'),
                    confirmDeleteSingleText: Translate(
                        'image-processing/page/list/thumbnail/modal/delete-text'
                    ),
                    confirmDeleteSingleAcceptButton: Translate(
                        'delete/modal/footer/button-label/delete'
                    ),
                    confirmDeleteSingleCancelButton: Translate(
                        'delete/modal/footer/button-label/cancel'
                    ),
                    deleteMultipleButton: Translate(
                        'image-processing/button-label/delete-multiple/edit'
                    ),
                    selectAllButton: Translate(
                        'image-processing/button-label/select-many/select-all'
                    ),
                    deselectAllButton: Translate(
                        'image-processing/button-label/select-many/deselect-all'
                    ),
                    downloadImagesButton: Translate(
                        'image-processing/unedited/button-label/download-images'
                    ),
                    deliverImagesButton: Translate(
                        'image-processing/deliver/panel/form/button-label/deliver'
                    ),
                    customerSendInButton: Translate('digital-styling/button-label/send-in'),
                    customerSendInWithoutMaterialButton: Translate(
                        'digital-styling/button-label/skip-upload'
                    )
                }}
                orderId={orderId}
            />
        );
    }

    if (isCustomer) {
        return (
            <DigitalStylingOrderCustomerView
                orderId={orderId}
                labels={{
                    customerTab: Translate('digital-styling/tab/customer-images'),
                    downloadHighResolutionButton: Translate(
                        'image-processing/edited/button-label/download-high-images'
                    ),
                    downloadNormalResolutionButton: Translate(
                        'image-processing/edited/button-label/download-normal-images'
                    ),
                    orderInProgress: Translate('digital-styling/status/in-progress')
                }}
            />
        );
    } else {
        return (
            <DigitalStylingOrderAdminView
                labels={{
                    filesAdded: Translate('photographer-uploads/order/row/files-added'),
                    editedTab: Translate('image-processing/page/tab/edited-images'),
                    uneditedTab: Translate('image-processing/page/tab/unedited-images'),
                    defaultMessage: Translate('dropzone/title'),
                    fallbackMessage: Translate('dropzone/fallback'),
                    invalidFileType: Translate('dropzone/invalid-filetype'),
                    cancelUpload: Translate('dropzone/cancel-upload'),
                    cancelUploadConfirmation: Translate('dropzone/cancel-upload-confirmation'),
                    uploadButton: Translate('dropzone/button/upload'),
                    failedUploadsHeading: Translate('image-processing/header/failed-images'),
                    uploadsInProgressHeading: Translate(
                        'image-processing/header/uploads-in-progress'
                    ),
                    thumbnailTooltipDelete: Translate(
                        'image-processing/page/list/thumbnail/title/delete'
                    ),
                    confirmDeleteSingleTitle: Translate('delete/modal/title/delete'),
                    confirmDeleteSingleText: Translate(
                        'image-processing/page/list/thumbnail/modal/delete-text'
                    ),
                    confirmDeleteSingleAcceptButton: Translate(
                        'delete/modal/footer/button-label/delete'
                    ),
                    confirmDeleteSingleCancelButton: Translate(
                        'delete/modal/footer/button-label/cancel'
                    ),
                    deleteMultipleButton: Translate(
                        'image-processing/button-label/delete-multiple/edit'
                    ),
                    selectAllButton: Translate(
                        'image-processing/button-label/select-many/select-all'
                    ),
                    deselectAllButton: Translate(
                        'image-processing/button-label/select-many/deselect-all'
                    ),
                    // If we want to add similar step for image editors we probably want an approve step
                    // approveImagesButton: Translate('digital-styling/button-label/approve-images'),
                    downloadImagesButton: Translate(
                        'image-processing/unedited/button-label/download-images'
                    ),
                    deliverImagesButton: Translate(
                        'image-processing/deliver/panel/form/button-label/deliver'
                    )
                }}
                orderId={orderId}
            />
        );
    }

    return null;
}

export default function DigitalStylingCustomerViewDelegateDataLayer({
    isCustomer,
    isNewOrder,
    orderId,
    userRole
}: ExternalProps) {
    let queryKey = 'digital-styling-customer-view-delegate';
    let { data, isLoading, isError, error } = useQuery([queryKey], () =>
        CustomerDigitalStylingApi.getData(orderId)
    );

    if (isLoading) {
        return (
            <fieldset>
                <Skeleton
                    variant="text"
                    sx={{ fontSize: '1.4rem', padding: '20px 20px 10px 20px' }}
                />
            </fieldset>
        );
    }
    
    if (isError) {
      console.error('Error loading digital styling data in customer delegate', error);
    }

    let { orderStatus } =
        data && data.orderStatus ? data : { orderStatus: DigitalStylingStatus.Unknown };

    return (
        <DigitalStylingCustomerViewDelegate
            orderStatus={orderStatus.value}
            isCustomer={isCustomer}
            isNewOrder={isNewOrder}
            orderId={orderId}
            userRole={userRole}
        />
    );
}
